/* =============================================================================
   Rynoka — Article (blog detail) page behaviours
   Loaded by sections/main-article.liquid.

   Features:
     1. Reading progress bar (top, fills as user scrolls article body)
     2. Table of Contents (auto-built from h2/h3 inside .tl-article__content)
        with smooth-scroll + scroll-spy active highlighting
     3. Copy-link share buttons (clipboard API with visual confirmation)
     4. Native Web Share fallback for mobile users on the chip group
   ============================================================================ */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ── 1. Reading progress bar ────────────────────────────────────────────
  function setupProgressBar() {
    var bar = document.querySelector('[data-tl-article-progress-bar]');
    var content = document.querySelector('[data-tl-article-content]');
    if (!bar || !content) return;

    var ticking = false;

    function update() {
      var rect = content.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var total = rect.height - viewport;
      var scrolled = -rect.top;
      var pct = 0;
      if (total > 0) {
        pct = Math.max(0, Math.min(1, scrolled / total));
      } else if (rect.top < 0) {
        pct = 1;
      }
      bar.style.width = (pct * 100).toFixed(2) + '%';
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  // ── 2. Auto Table of Contents + scroll-spy ─────────────────────────────
  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function setupToc() {
    var content = document.querySelector('[data-tl-article-content]');
    var toc = document.querySelector('[data-tl-article-toc]');
    var list = document.querySelector('[data-tl-article-toc-list]');
    if (!content || !toc || !list) return;

    var headings = content.querySelectorAll('h2, h3');
    if (!headings.length) {
      toc.setAttribute('hidden', '');
      return;
    }

    var seen = {};
    var entries = [];
    headings.forEach(function (h) {
      var text = (h.textContent || '').trim();
      if (!text) return;
      var base = h.id || slugify(text) || 'section';
      var id = base, n = 2;
      while (seen[id]) { id = base + '-' + n++; }
      seen[id] = true;
      h.id = id;
      entries.push({ id: id, text: text, level: h.tagName === 'H3' ? 3 : 2, el: h });
    });

    if (!entries.length) {
      toc.setAttribute('hidden', '');
      return;
    }

    list.innerHTML = '';
    entries.forEach(function (e) {
      var li = document.createElement('li');
      li.className = e.level === 3 ? 'tl-article__toc-h3' : 'tl-article__toc-h2';
      var a = document.createElement('a');
      a.href = '#' + e.id;
      a.textContent = e.text;
      a.setAttribute('data-tl-toc-link', e.id);
      li.appendChild(a);
      list.appendChild(li);
    });

    toc.removeAttribute('hidden');

    // Smooth scroll
    list.addEventListener('click', function (ev) {
      var a = ev.target.closest('a[href^="#"]');
      if (!a) return;
      var target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      ev.preventDefault();
      var offset = 80;
      var y = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      history.replaceState(null, '', a.getAttribute('href'));
    });

    // Scroll-spy with IntersectionObserver
    var links = list.querySelectorAll('a[data-tl-toc-link]');
    var byId = {};
    links.forEach(function (l) { byId[l.getAttribute('data-tl-toc-link')] = l; });

    var visible = {};
    function activate(id) {
      links.forEach(function (l) { l.classList.remove('is-active'); });
      if (id && byId[id]) byId[id].classList.add('is-active');
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entriesIO) {
        entriesIO.forEach(function (en) {
          visible[en.target.id] = en.isIntersecting;
        });
        // Pick the first heading that's currently within the viewport's top band
        var current = null;
        for (var i = 0; i < entries.length; i++) {
          if (visible[entries[i].id]) { current = entries[i].id; break; }
        }
        if (!current) {
          // fallback: nearest heading above the viewport top
          for (var j = entries.length - 1; j >= 0; j--) {
            var rect = entries[j].el.getBoundingClientRect();
            if (rect.top < 120) { current = entries[j].id; break; }
          }
        }
        activate(current);
      }, {
        rootMargin: '-100px 0px -65% 0px',
        threshold: [0, 1]
      });

      entries.forEach(function (e) { io.observe(e.el); });
    }
  }

  // ── 3. Copy-link share ─────────────────────────────────────────────────
  function setupCopyShare() {
    var nodes = document.querySelectorAll('[data-tl-share-copy]');
    if (!nodes.length) return;

    nodes.forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        var url = btn.getAttribute('data-tl-share-copy') || window.location.href;
        var done = function () {
          btn.classList.add('is-copied');
          var label = btn.querySelector('[data-tl-share-copy-text]');
          var prev;
          if (label) { prev = label.textContent; label.textContent = 'Copied!'; }
          window.setTimeout(function () {
            btn.classList.remove('is-copied');
            if (label && prev) label.textContent = prev;
          }, 1800);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done).catch(function () { fallback(url, done); });
        } else {
          fallback(url, done);
        }
      });
    });

    function fallback(text, done) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch (e) {
        // last-resort: open prompt
        window.prompt('Copy this link', text);
      }
    }
  }

  // ── 4. Native Web Share if available (delegates from share chips) ──────
  function setupWebShare() {
    if (!navigator.share) return;

    var triggers = document.querySelectorAll('.tl-article__share-chip[data-tl-share-copy]');
    triggers.forEach(function (btn) {
      btn.addEventListener('contextmenu', function (ev) {
        ev.preventDefault();
        navigator.share({
          title: document.title,
          url: btn.getAttribute('data-tl-share-copy') || window.location.href
        }).catch(function () {});
      });
    });
  }

  ready(function () {
    setupProgressBar();
    setupToc();
    setupCopyShare();
    setupWebShare();
  });
})();
